import { Constants } from '../../common/Constants';
import { DEFAULT_PRINT_DIRECTORY_PDF_CONTENT } from './PrintDirectoryPage.constants';
import type { PrintDirectoryPdfContent } from './PrintDirectoryPage.constants';
import type {
  PrintableProvider,
  PrintLabels,
  ProviderAddress,
  ProviderAffiliation,
  ProviderHours,
  ProviderPhone,
  ProviderPrintContent,
  ResolvedPdfContentConfig,
} from './PrintPdfGenerator.types';

const DAY_ABBREVIATIONS: Record<string, string> = {
  sunday: 'Sun',
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
};

// ── Primitive helpers ──────────────────────────────────────────────────

export const escapeHtml = (
  value: string | number | null | undefined
): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const formatDate = (): string =>
  new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
    .format(new Date())
    .replace(/\//g, '.');

export const toStringValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }
  if (
    typeof value === 'object' &&
    'value' in (value as Record<string, unknown>)
  ) {
    return toStringValue((value as Record<string, unknown>).value);
  }
  return '';
};

export const toStringArray = (value: unknown): string[] => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value.map((item) => toStringValue(item)).filter(Boolean);
  }
  const singleValue = toStringValue(value);
  return singleValue ? [singleValue] : [];
};

export const dedupeValues = (values: string[]): string[] =>
  Array.from(new Set(values.filter(Boolean)));

export const normalizeList = (value?: string | string[]): string[] =>
  toStringArray(value);

// ── Formatting helpers ─────────────────────────────────────────────────

export const formatTime = (value?: string): string => {
  const trimmedValue = String(value || '').trim();
  if (!trimmedValue) return '';

  const match = trimmedValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return trimmedValue;

  const [, rawHours, minutes] = match;
  const hourNumber = Number.parseInt(rawHours, 10);

  if (Number.isNaN(hourNumber)) return trimmedValue;

  const meridiem = hourNumber >= 12 ? 'PM' : 'AM';
  const normalizedHour = hourNumber % 12 || 12;

  return `${normalizedHour}:${minutes} ${meridiem}`;
};

export const formatPhoneNumber = (value: string): string => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return '';

  const digitsOnly = trimmedValue.replace(/\D/g, '');
  const normalizedDigits =
    digitsOnly.length === 11 && digitsOnly.startsWith('1')
      ? digitsOnly.slice(1)
      : digitsOnly;

  if (normalizedDigits.length !== 10) {
    return trimmedValue;
  }

  return `(${normalizedDigits.slice(0, 3)}) ${normalizedDigits.slice(
    3,
    6
  )}-${normalizedDigits.slice(6)}`;
};

export const formatDistance = (
  distance: number | string | undefined
): string => {
  if (distance === undefined || distance === null || distance === '') return '';

  const numeric =
    typeof distance === 'number' ? distance : Number.parseFloat(distance);
  if (Number.isNaN(numeric)) return String(distance);

  return `${numeric.toFixed(1)} miles away`;
};

export const normalizePhones = (value: unknown): ProviderPhone[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter(Boolean) as ProviderPhone[];
  }
  if (typeof value === 'object') {
    return [value as ProviderPhone];
  }
  return [];
};

// ── PDF content config ─────────────────────────────────────────────────

export const resolvePdfContentConfig = (
  content?: PrintDirectoryPdfContent
): ResolvedPdfContentConfig => ({
  introText: content?.introText?.length
    ? content.introText
    : DEFAULT_PRINT_DIRECTORY_PDF_CONTENT.introText,
  memberServicesText:
    content?.memberServicesText ||
    DEFAULT_PRINT_DIRECTORY_PDF_CONTENT.memberServicesText,
  resourceItems: content?.resourceItems?.length
    ? content.resourceItems
    : DEFAULT_PRINT_DIRECTORY_PDF_CONTENT.resourceItems,
  premiumText:
    content?.premiumText || DEFAULT_PRINT_DIRECTORY_PDF_CONTENT.premiumText,
  premiumEmphasisText:
    content?.premiumEmphasisText ||
    DEFAULT_PRINT_DIRECTORY_PDF_CONTENT.premiumEmphasisText,
  hospitalText:
    content?.hospitalText || DEFAULT_PRINT_DIRECTORY_PDF_CONTENT.hospitalText,
  tierText: content?.tierText || DEFAULT_PRINT_DIRECTORY_PDF_CONTENT.tierText,
  referralNoteText:
    content?.referralNoteText ||
    DEFAULT_PRINT_DIRECTORY_PDF_CONTENT.referralNoteText,
  footerText: content?.footerText?.length
    ? content.footerText
    : DEFAULT_PRINT_DIRECTORY_PDF_CONTENT.footerText,
});

// ── Provider data extraction ───────────────────────────────────────────

export const getProviderName = (provider: PrintableProvider): string => {
  if (provider.providerName) return provider.providerName;

  const parts = [provider.lastName, provider.firstName, provider.middleName]
    .filter(Boolean)
    .map((part) => String(part).trim())
    .filter(Boolean);

  if (parts.length === 0) return '';

  const [lastName, ...rest] = parts;
  return [lastName, rest.join(' ')].filter(Boolean).join(', ');
};

export const getDegrees = (provider: PrintableProvider): string =>
  normalizeList(provider.primaryDegrees).join(', ');

export const getSpecialties = (provider: PrintableProvider): string[] =>
  [
    ...normalizeList(provider.primarySpecialities),
    ...normalizeList(provider.speciality),
  ].filter(Boolean);

export const getLocationHeading = (provider: PrintableProvider): string => {
  const city = provider.address?.city?.trim();
  const district = provider.address?.district?.trim();
  const state = provider.address?.state?.trim();

  return [city, district, state]
    .filter(Boolean)
    .map((part) => part?.toUpperCase())
    .join(', ');
};

export const getSpecialtyHeading = (provider: PrintableProvider): string =>
  getSpecialties(provider)[0]?.toUpperCase() || '';

export const getAddressLines = (address?: ProviderAddress): string[] => {
  if (!address) return [];

  const line = normalizeList(address.line).join(', ').trim();
  const cityStateZip = [address.city, address.state, address.postalCode]
    .filter(Boolean)
    .join(' ')
    .trim();

  return [address.locationName, line, cityStateZip].filter(
    Boolean
  ) as string[];
};

export const getAffiliations = (
  affiliations?: ProviderAffiliation[]
): string =>
  toStringArray(affiliations?.map((item) => item?.value)).join(', ');

export const isVirtualVisitOnlyProvider = (
  provider: PrintableProvider
): boolean => {
  const locationValues = [
    provider.locationName,
    provider.address?.locationName,
    ...toStringArray(provider.address?.line),
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  return locationValues.some(
    (value) =>
      value.includes('virtual visit only') ||
      value.includes(Constants.VIRTUAL_VISIT.toLowerCase())
  );
};

export const getAppointmentLines = (provider: PrintableProvider): string[] => {
  const lines: string[] = [];
  if (provider.hasEveningAppointments) lines.push('Evening Appointments');
  if (provider.hasWeekendAppointment) lines.push('Weekend Appointments');
  return lines;
};

export const getReadableLanguageValues = (values: unknown): string[] =>
  dedupeValues(toStringArray(values));

export const getMedicalStaff = (provider: PrintableProvider): string =>
  getReadableLanguageValues(provider.languagesSpokenByOffice).join(', ') ||
  getReadableLanguageValues(provider.languagesSpoken).join(', ') ||
  getReadableLanguageValues(provider.languagesSpokenByOfficeWithCodes).join(
    ', '
  ) ||
  getReadableLanguageValues(provider.languagesSpokenWithCodes).join(', ');

export const getPracticeLimit = (provider: PrintableProvider): string =>
  toStringArray([
    provider.practiceLimitations?.age,
    provider.practiceLimitations?.gender,
  ]).join(' | ');

export const getReferralIndicator = (provider: PrintableProvider): string =>
  toStringArray(provider.referralIndicator).join(', ');

export const buildIdentityLine = (
  labels: PrintLabels,
  licenseNumbers: string,
  npi?: string
): string =>
  [
    licenseNumbers
      ? `${labels.licenseLabel || 'License'} #: ${licenseNumbers}`
      : '',
    npi ? `${labels.npiLabel || 'NPI'}: ${npi}` : '',
  ]
    .filter(Boolean)
    .join(' | ');

export const buildPhoneLines = (
  phones: unknown,
  labels: PrintLabels
): string[] => {
  const phoneLabel = labels.phoneLabel || 'Phone';
  const faxLabel = labels.faxLabel || 'Fax';
  const emergencyLabel = labels.emergencyLabel || 'Emergency';

  return normalizePhones(phones).flatMap((phone) => [
    ...toStringArray(phone?.phone).map(
      (value) => `${formatPhoneNumber(value)} ${phoneLabel}`
    ),
    ...toStringArray(phone?.appointment).map(
      (value) => `${formatPhoneNumber(value)} ${phoneLabel}`
    ),
    ...toStringArray(phone?.fax).map(
      (value) => `${formatPhoneNumber(value)} ${faxLabel}`
    ),
    ...toStringArray(phone?.emergency).map(
      (value) => `${formatPhoneNumber(value)} ${emergencyLabel}`
    ),
  ]);
};

export const getProviderPrintContent = (
  provider: PrintableProvider,
  labels: PrintLabels
): ProviderPrintContent => {
  const specialties = getSpecialties(provider);
  const licenseNumbers = normalizeList(provider.licenseNumber).join(', ');

  return {
    allSpecialties: specialties.join(', '),
    appointmentLines: getAppointmentLines(provider),
    addressLines: getAddressLines(provider.address),
    boardCertifications: normalizeList(provider.boardCertifications).join(', '),
    hospitalAffiliations: getAffiliations(provider.hospitalAffiliations),
    identityLine: buildIdentityLine(labels, licenseNumbers, provider.npi),
    isVirtualVisitOnly: isVirtualVisitOnlyProvider(provider),
    medicalStaff: getMedicalStaff(provider),
    nameLine: [getProviderName(provider) || 'N/A', getDegrees(provider)]
      .filter(Boolean)
      .join(', '),
    phones: buildPhoneLines(provider.phones, labels),
    practiceLimit: getPracticeLimit(provider),
    providerGroups: getAffiliations(provider.providerGroupsAffiliations),
    referralIndicator: getReferralIndicator(provider),
  };
};

// ── HTML rendering helpers ─────────────────────────────────────────────

export const renderHours = (
  hours?: ProviderHours[],
  dayAbbreviations?: Record<string, string>,
  closedLabel: string = 'Closed'
): string => {
  if (!hours?.length) return '';

  const rows = hours
    .filter((item) => item?.day)
    .map((item) => {
      const normalizedDay = String(item.day || '').trim().toLowerCase();
      const dayLabel =
        dayAbbreviations?.[normalizedDay] ||
        DAY_ABBREVIATIONS[normalizedDay] ||
        normalizedDay.slice(0, 3).replace(/^./, (char) => char.toUpperCase());
      const timeRange =
        item.openingTime && item.closingTime
          ? `${formatTime(item.openingTime)} - ${formatTime(item.closingTime)}`
          : closedLabel;

      return `<div class="hours-row"><span class="hours-day">${escapeHtml(
        `${dayLabel}:`
      )}</span><span class="hours-time">${escapeHtml(timeRange)}</span></div>`;
    })
    .join('');

  return rows ? `<div class="hours-block">${rows}</div>` : '';
};

export const renderSimpleList = (
  values: string[],
  className: string
): string => {
  if (values.length === 0) return '';

  return values
    .map((value) => `<div class="${className}">${escapeHtml(value)}</div>`)
    .join('');
};

export const renderSideMeta = (label: string, value?: string): string => {
  if (!value) return '';

  return `<div class="provider-side-row"><span class="provider-side-label">${escapeHtml(
    label
  )}</span>${escapeHtml(value)}</div>`;
};

export const hasReferralNeededProviders = (
  providers: PrintableProvider[]
): boolean =>
  providers.some(
    (provider) => toStringArray(provider.referralIndicator).length
  );

export const writeHtmlToWindow = (
  printWindow: Window | null,
  html: string
): void => {
  if (!printWindow) return;

  printWindow.document.open('text/html', 'replace');
  printWindow.document.write(html);
  printWindow.document.close();
};
